import React, { useRef, useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../Services/translation';
import {
    query,
    setSearchQuery,
    toggleShowFullForm,
    handleKeyDown,
    handleClickSearch,
} from '../ActionsBrafab/QuickSearch.action';
import usePrevious from '@react-hook/previous';
import { debounce } from 'lodash-es';

const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);
const QuickSearchResult = lazy(() => import('./QuickSearchResult'));

export default function QuickSearch() {
    const {
        query,
        result,
        linkText,
        productsCount,
        sparePartsCount,
        inspirationPageCount,
        pagesCount,
        showResult,
        showFullForm,
        onSearch,
        selectedItem,
    } = useSelector((state) => state.quickSearch);

    const [lastClickedNode, setLastClickedNode] = useState(null);
    const searchUrl = window.__litium.quickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const previousSelectedItem = usePrevious(selectedItem);
    const [activeType, setActive] = useState('products');
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const clickHandler = useCallback((event) => {
        setLastClickedNode(event.target);
    }, []);
    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        // set query value if it is avaialble in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem !== previousSelectedItem) {
            const el = document.querySelector('.quick-search-result__item--selected');
            el &&
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
        }
    }, [selectedItem, previousSelectedItem]);

    function getWindowDimensions() {
        const { outerWidth: width, innerHeight: height } = window;
        return {
            width,
            height
        };
    }

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSearchInput = (e) => {
        e.preventDefault();
        dispatch(toggleShowFullForm());
        setTimeout(() => {
            searchInput.current && searchInput.current.focus();
        }, 0);
    };

    const isTabletDown = windowDimensions.width < 1024;

    return (
        <div className="quick-search" role="search" ref={searchContainer} >
            <button
                className="quick-search__link--block"
                title={translate('general.search')}
                area-label={translate('general.search')}
                onClick={(e) => {
                    toggleSearchInput(e);
                }}
            >
                <i className="quick-search__icon"></i>
            </button>
            <div className="quick-search__wrapper">
                <div className={`quick-search__form ${showFullForm ? 'quick-search__form--force-show' : null}`} role="search">
                    <i className="quick-search__icon quick-search__icon--form " onClick={(e) => { toggleSearchInput(e); }}></i>
                    <input
                        id="quickSearchContainer"
                        className="quick-search__input"
                        type="search"
                        placeholder={translate('general.search')}
                        autoComplete="off"
                        value={decodeURIComponent(query)}
                        onChange={() => {
                            const text = encodeURIComponent(searchInput.current.value);
                            dispatch(setSearchQuery(text));
                            debouncedQuery(dispatch, text);
                        }}
                        onKeyDown={(e) => dispatch(handleKeyDown(e, { searchUrl }))}
                        ref={searchInput}
                        onBlur={() => {
                            if (
                                searchContainer.current &&
                                !searchContainer.current.contains(lastClickedNode)
                            ) {
                                showFullForm && dispatch(toggleShowFullForm());
                            }
                        }}
                        onMouseDown={() => isTabletDown ? dispatch(toggleShowFullForm()) : null}
                    />
                    <button
                        className="quick-search__submit-button"
                        type="submit"
                        onClick={(e) => {
                            e.preventDefault();
                            dispatch(handleClickSearch({ searchUrl }));
                        }}
                    >
                        <i className="quick-search__submit-icon"></i>
                        <span className="quick-search__submit-title">
                            {translate('general.search')}
                        </span>
                    </button>
                    {showResult && (
                        <Suspense fallback={null}>
                            <QuickSearchResult
                                activeType={activeType}
                                setActive={(category) => setActive(category)}
                                onSearch={(category) => onSearch(category)}
                                productsCount={productsCount}
                                sparePartsCount={sparePartsCount}
                                inspirationPageCount={inspirationPageCount}
                                pagesCount={pagesCount}
                                result={result}
                                linkText={linkText}
                                selectedItem={selectedItem}
                                searchUrl={searchUrl}
                                toggleShowFullForm={toggleShowFullForm}
                            />
                        </Suspense>
                    )}
                </div>
            </div>
        </div>
    );
}
